// pages/overlay/Break.jsx
// Three-column BRB: left panel, center transparent video window, right panel.
// Center = the viewport minus two side columns (each (100vw−100vh)/2 capped
// at 18vw — wider than the old exact square on 16:9 so the reel shows more).
// No bottom bar. No center frame overlay. Gold accent on inner column edges.

import { useEffect, useState } from "react";
import useOrientation from "./../../hooks/useOrientation";
import SummonBanner from "./../../components/overlay/SummonBanner";
import useStreamConfig from "./../../hooks/useStreamConfig";
import useCurrentShiftOrders from "./../../hooks/useCurrentShiftOrders";
import useFlashDesigns from "./../../hooks/useFlashDesigns";
import useObsActive from "./../../hooks/useObsActive";
import useOverlayPalette from "./../../hooks/useOverlayPalette";
import useBoothShowcase from "./../../hooks/useBoothShowcase";
import { filterMarqueeForShift } from "./../../utils/marqueeContext";
import { computeQueueWait, fmtWait } from "./../../utils/overlayFormat";
import JoPLogo from "./../../components/overlay/JoPLogo";
import MarqueeTicker from "./../../components/overlay/MarqueeTicker";
import FlashDesignsPanel from "./../../components/overlay/FlashDesignsPanel";
import PromoPanel from "./../../components/overlay/PromoPanel";
import { AscendingDust, FlashBrandBand, IdleCaptionBar, IdleQueueChip } from "./../../components/overlay/IdleSceneKit";

const D = "var(--jop-display)";
const COL_BG = "linear-gradient(180deg, var(--jop-grad-1) 0%, var(--jop-grad-2) 50%, var(--jop-grad-3) 100%)";
const CREAM = "var(--jop-cream)";
const BLUE = "var(--jop-blue)";
const GOLD = "var(--jop-gold)";

// Column width: (viewport - square) / 2, capped at 18vw so the reel window
// widens past the old exact square on 16:9 rigs (columns were 420px at
// 1080p — the reel behind is the draw; 345px still fits every panel).
const COL_W = "min(calc((100vw - 100vh) / 2), 18vw)";

// Portrait (IG Live cut, 2026-07-03; decluttered 2026-08-29): this scene
// exists to fill dead air with a sizzle reel. The reel is an OBS media source
// running full-screen BEHIND this overlay; everything except a compact top
// group (logo + BRB pill + queue chip), the bottom caption bar, and the flash
// band is transparent so the reel carries the screen. Same IG safe-zone union
// as Main.jsx (top ~250 host bar / bottom ~600 comments / right ~130): the
// pill group sits just below the host bar; the caption bar + brand band are
// the sacrificial comment zone (booth panel shows them in full). Fun facts +
// marquee rotate in the caption bar; flash menu / spotlight are landscape-only
// column content (portrait's flash menu is the band grid).

// ── DID YOU KNOW ────────────────────────────────────────────────────────────
const FACTS = [
  "Dan's first pancake art was a smiley face made on a graveyard shift at Courtesy Diner in 2009. The customer left a $20 tip on a $3 stack.",
  "Dan draws on a mirrored reference image. He's not working in reverse — he's drawing exactly what he sees, layered like glass painting.",
  "The batter is intentionally overmixed and much thinner than normal pancake batter. It has to flow through fine-tipped detail bottles without clogging.",
  "Dan etches a grid directly into his griddle. The grid gives him rigid guidelines to compose complex portraits live in front of an audience.",
  "The color in these pancakes comes from gel food coloring, not heat. A totally different technique called 'heat toning' uses browning for shading. Dan moved past that over a decade ago.",
  "Pancakes often look rough from the drawing side and stunning when flipped. You never fully know what you've got until the reveal.",
  "After 17 years, the flip is like breathing. The high Dan's chasing is your reaction.",
  "The hardest pancake Dan ever made was Bill Hader. Weeks of multi-hour attempts, and something about his face just refused to cooperate.",
  "The biggest pancake Dan ever made was a 4-foot by 4-foot power-up mushroom. He assembled four full-griddle quadrants into one piece.",
  "Group photos are the hardest requests. Multiple faces in a small space is exponentially harder than a single portrait.",
  "A crossed-eyed Jaime Lannister once ended a 2-hour portrait session. Dan had to sit down after that flip.",
  "A kid once requested a random Minecraft screenshot. Just the side of a hill with nothing going on. He was thrilled with it.",
  "Memorial pancakes for lost pets and loved ones are a regular request. Dan considers it a privilege to be entrusted with someone's grief.",
];

// Landscape-only card since 2026-08-29 — the portrait cut rotates the same
// FACTS through the bottom IdleCaptionBar instead (the mid-stack floating
// card blanketed the reel). Fonts sized for the narrowed 18vw column.
function DidYouKnow({ fill = false }) {
  const [idx, setIdx] = useState(0);
  const [vis, setVis] = useState(true);
  useEffect(() => {
    const t = setInterval(() => {
      setVis(false);
      setTimeout(() => { setIdx(i => (i + 1) % FACTS.length); setVis(true); }, 500);
    }, 9500);
    return () => clearInterval(t);
  }, []);
  return (
    <div style={{
      background: CREAM,
      border: `2px solid ${GOLD}`,
      borderRadius: "14px",
      padding: "1rem 1.3rem",
      boxShadow: "0 0 12px rgba(var(--jop-gold-rgb), 0.15)",
      // Rigid footprint: the card never grows/shrinks with fact length, so
      // the marketing spotlight above keeps a stable slice of the column.
      // fill = take the parent's slot instead.
      height: fill ? "100%" : "clamp(250px, 30vh, 340px)",
      boxSizing: "border-box",
      display: "flex", flexDirection: "column",
      opacity: vis ? 1 : 0, transition: "opacity 0.5s ease",
    }}>
      <div style={{
        color: BLUE, fontFamily: D, fontWeight: 700,
        fontSize: "1.6rem", textTransform: "uppercase",
        letterSpacing: "0.08em", marginBottom: "0.5rem",
        flexShrink: 0,
      }}>🥞 Did You Know?</div>
      <div style={{
        flex: 1, minHeight: 0,
        display: "flex", alignItems: "center",
        overflow: "hidden",
      }}>
        <div style={{
          color: "#1a1a2e",
          fontSize: "1.3rem", lineHeight: 1.4,
        }}>{FACTS[idx]}</div>
      </div>
    </div>
  );
}

// ── BRB BADGE ───────────────────────────────────────────────────────────────
// Shared by the landscape left column and the portrait top band.
function BrbBadge({ breakMessage, breakReturnTime, active, fontSize = "1.15rem" }) {
  return (
    <div style={{
      background: CREAM, color: BLUE,
      fontFamily: D, fontWeight: 700,
      fontSize, textAlign: "center", lineHeight: 1.4,
      padding: "0.8rem 1rem", borderRadius: "16px",
      border: `3px solid ${GOLD}`,
      boxShadow: "0 0 22px rgba(var(--jop-gold-rgb), 0.28)",
      flexShrink: 0, position: "relative", zIndex: 1,
      animation: "jopBrbPulse 3s ease-in-out infinite",
      animationPlayState: active ? "running" : "paused",
    }}>
      {/* NBSP before the trailing ✦ — at pill scale the message can wrap,
          and a lone orphaned ✦ on its own line reads broken. */}
      ✦ {breakMessage}{" ✦"}
      {breakReturnTime && (
        // em, not rem — scales with the badge's fontSize prop, so the
        // portrait cut's bigger pill carries a proportionally bigger line
        // (rem left this at 13.6px on the 1080×1920 canvas: unreadable on a
        // phone stream or from across the market).
        <div style={{ fontSize: "0.7em", fontWeight: 600, opacity: 0.8, marginTop: "0.25rem" }}>
          ⏱ Back in: {breakReturnTime}
        </div>
      )}
    </div>
  );
}

// ── MAIN ─────────────────────────────────────────────────────────────────────
export default function Break() {
  useOverlayPalette();
  const orientation = useOrientation();
  const portrait = orientation === "portrait";
  const active = useObsActive();
  const config = useStreamConfig();
  const { marqueeMessages } = config;
  const { activeShift, drawing, cookingAll, queued, allActive, concurrentCount } = useCurrentShiftOrders();
  // Flash menu must stay visible at all times during service — same per-event
  // board as Booth/Main/StartingSoon/Reference. Pass activeShift so per-event
  // flash profiles (shift.activeFlashProfileIds) resolve, not just the base IP set.
  const { allDesigns: flashDesigns } = useFlashDesigns(activeShift, { previewWhenIdle: true });
  // Apply per-shift override (no context filter — Break shows during stream
  // phases and historically passes the full marquee through). "both" mode
  // skips context filtering, leaving only the disabled-text override layer.
  const filteredMarquee = filterMarqueeForShift(marqueeMessages, "both", activeShift);
  // Marketing spotlight — same rotating boothShowcase queue as /overlay/booth
  // (respects per-shift disabledShowcaseIds).
  const { queue: showcaseQueue } = useBoothShowcase();

  const queueDepth = [drawing, ...(cookingAll || []), ...(queued || [])].filter(Boolean).length;
  const waitMin = computeQueueWait(allActive, activeShift);
  // The ordering QR itself is the SummonBanner layer (resolveQrFlow) mounted
  // on the reel region below — this gate only tunes the BRB copy.
  const joinOpen = !!activeShift?.isQueueOpen && !!activeShift?.livestreamJoinEnabled;

  // Operator copy always wins; the default leans CTA when the join gate is
  // open (get people into the queue during dead air) and stays reassuring
  // when it isn't.
  const breakMessage = config.breakMessage?.trim()
    || (joinOpen ? "Back in a few — get in the queue!" : "Back in a few — don't lose your spot!");
  const breakReturnTime = config.breakReturnTime?.trim() || "";

  return (
    <div style={{
      width: "100vw", height: "100vh",
      background: "transparent",
      fontFamily: "system-ui, -apple-system, sans-serif",
      fontSize: "calc(18px + 0.7vw)",
      userSelect: "none", overflow: "hidden", position: "relative",
    }}>

      {/* Ordering QR — flow-resolved SummonBanner layer, anchored to the
          feed region so it reads as living on the reel/camera video and
          never touches the columns/bands or the IG host-bar/comment zones.
          Portrait: below the pill stack, floor at y≈1320 (safe-zone bottom).
          Landscape: the center camera square between the columns. */}
      <div style={portrait
        ? { position: "absolute", left: 0, right: 0, top: "280px", bottom: "31.5vh", zIndex: 3 }
        : { position: "absolute", top: 0, bottom: 0, left: COL_W, right: COL_W, zIndex: 3 }
      }>
        <SummonBanner
          scene="break"
          shift={activeShift}
          concurrentCount={concurrentCount}
          streamConfig={config}
          queueDepth={queueDepth}
          waitMin={waitMin}
          portrait={portrait}
          active={active}
        />
      </div>

      {/* ═══ LEFT COLUMN ═══════════════════════════════════════════════════ */}
      {!portrait && (
        <div style={{
          position: "absolute", top: 0, left: 0, bottom: 0,
          width: COL_W, background: COL_BG,
          // Gold inner edge (right side of left column)
          borderRight: `3px solid ${GOLD}`,
          boxShadow: "inset -8px 0 20px rgba(var(--jop-gold-rgb), 0.08)",
          display: "flex", flexDirection: "column",
          justifyContent: "space-between",
          padding: "1.4rem 1.1rem",
          zIndex: 2, overflow: "hidden",
        }}>
          <AscendingDust count={8} seed={88} active={active} />

          {/* 1. Logo — 130 (was 160) for the narrowed 18vw column */}
          <div style={{ textAlign: "center", flexShrink: 0, position: "relative", zIndex: 1 }}>
            <JoPLogo variant="wide" height={130} animated />
          </div>

          {/* 2. BRB Badge */}
          <BrbBadge breakMessage={breakMessage} breakReturnTime={breakReturnTime} active={active} />

          {/* 3. Flash menu — visible at all times during service */}
          <div style={{
            background: "rgba(0,0,0,0.2)",
            border: "1px solid rgba(var(--jop-gold-rgb), 0.25)",
            borderRadius: "14px", padding: "0.8rem 0.9rem",
            flex: 1, minHeight: 0,
            display: "flex", flexDirection: "column",
            position: "relative", zIndex: 1, overflow: "hidden",
          }}>
            {flashDesigns && flashDesigns.length > 0 ? (
              <FlashDesignsPanel designs={flashDesigns} portrait={false} spotlight={!!config.boothGalaMode} />
            ) : (
              <div style={{
                flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
                color: CREAM, fontFamily: D, fontSize: "1rem",
                opacity: 0.45, textAlign: "center",
              }}>Your spot is safe!</div>
            )}
          </div>

          {/* 4. Marquee ticker — rigid height (2-line messages and cards must
              never reflow the flash panel above), content centered under the
              column. The box renders even with zero playable messages so the
              flash panel's slice of the column stays constant. */}
          <div style={{
            flexShrink: 0, position: "relative", zIndex: 1,
            height: "clamp(64px, 9vh, 110px)",
            marginTop: "0.3rem",
            display: "flex", alignItems: "center", justifyContent: "center",
            textAlign: "center",
            overflow: "hidden",
          }}>
            {filteredMarquee && (
              <MarqueeTicker messages={filteredMarquee} fontSize="1.4rem" color={CREAM} />
            )}
          </div>
        </div>
      )}

      {/* ═══ RIGHT COLUMN ══════════════════════════════════════════════════ */}
      {!portrait && (
        <div style={{
          position: "absolute", top: 0, right: 0, bottom: 0,
          width: COL_W, background: COL_BG,
          // Gold inner edge (left side of right column)
          borderLeft: `3px solid ${GOLD}`,
          boxShadow: "inset 8px 0 20px rgba(var(--jop-gold-rgb), 0.08)",
          display: "flex", flexDirection: "column",
          justifyContent: "space-between",
          gap: "1rem",
          padding: "1.4rem 1.1rem",
          zIndex: 2, overflow: "hidden",
        }}>
          <AscendingDust count={8} seed={99} active={active} />

          {/* 1. Marketing spotlight — rotating boothShowcase promo, same
              Griddle Gazette as /overlay/booth. Takes all flexible height
              (the QR stub was removed as misleading during a break; Did You
              Know below is rigid so this slice stays stable). */}
          <div style={{
            flex: 1, minHeight: 0,
            display: "flex", flexDirection: "column",
            position: "relative", zIndex: 1,
          }}>
            <PromoPanel queue={showcaseQueue} />
          </div>

          {/* 2. Did You Know — rigid height */}
          <div style={{ flexShrink: 0, position: "relative", zIndex: 1 }}>
            <DidYouKnow />
          </div>
        </div>
      )}

      {/* ═══ PORTRAIT (1080×1920) — IG Live sizzle-reel cut ═══════════════════
          The reel media source runs full-screen behind this overlay and it IS
          the show — the 2026-08-29 declutter cut the mid-stack Did You Know
          card + TickerPill so the reel's midfield (~y540–1350) stays open.
          Three overlay elements: a compact top group below IG Live's host-bar
          zone, the IdleCaptionBar riding the flash band's top edge (facts +
          marquee + URL in one rotation — below the IG-safe floor by design,
          the booth panel shows it in full), and the bottom flash band
          (24.5vh − 52px, all grid — its geometry is untouched so the flash
          grid never moves on a scene cut). */}
      {portrait && (
        <>
          {/* Top group — brand lockup, BRB pill, live queue chip. Compact:
              ends ~y540, first clear spot below the IG host bar (~250px).
              The ordering QR is the SummonBanner layer above, anchored to
              the reel region's bottom edge — no collision. */}
          <div style={{
            position: "absolute", top: "270px", left: 0, right: 0,
            display: "flex", flexDirection: "column", alignItems: "center",
            gap: "0.7rem",
            zIndex: 2, pointerEvents: "none",
          }}>
            <JoPLogo variant="wide" height={110} animated />
            <div style={{ width: "min(660px, calc(100vw - 200px))" }}>
              <BrbBadge
                breakMessage={breakMessage}
                breakReturnTime={breakReturnTime}
                active={active}
                fontSize="1.8rem"
              />
            </div>
            <IdleQueueChip
              open={!!activeShift?.isQueueOpen}
              queueDepth={queueDepth}
              waitMin={waitMin}
              fmtWait={fmtWait}
            />
          </div>

          {/* Caption bar — the ONE rotating text surface: per-shift marquee,
              Did You Know facts, and the URL beat, pinned above the band. */}
          <IdleCaptionBar
            messages={[
              ...(filteredMarquee || []),
              ...FACTS.map((f) => ({ text: `🥞 ${f}`, durationSec: 10 })),
            ]}
          />

          {/* Band logo + URL row dropped — the lockup and caption bar above
              carry brand + messaging; the band is all flash grid now. The
              52px shave keeps the grid box pixel-identical AND finally
              matches Booth's band height across a scene cut. */}
          <FlashBrandBand
            designs={flashDesigns}
            active={active}
            showLogo={false}
            showUrl={false}
          />
        </>
      )}

      <style>{`
        @keyframes jopBrbPulse {
          /* Scale-breath instead of an animated box-shadow. box-shadow is a
             paint property — animating it repaints the badge every frame.
             transform composites for free; the glow is now a constant. */
          0%, 100% { transform: scale(1); }
          50%      { transform: scale(1.025); }
        }
        @keyframes jopAscendDust {
          0%   { opacity: 0; transform: translateY(0) scale(0.6); }
          15%  { opacity: var(--jop-dust-peak, 0.5); }
          50%  { opacity: calc(var(--jop-dust-peak, 0.5) * 0.7); transform: translateY(-180px) scale(1); }
          85%  { opacity: calc(var(--jop-dust-peak, 0.5) * 0.2); }
          100% { opacity: 0; transform: translateY(-350px) scale(0.4); }
        }
      `}</style>
    </div>
  );
}
